import React from "react";
import { ShieldCheck, AlertTriangle, AlertOctagon, CircleDot, Clock, Ban, PauseCircle } from "lucide-react";

const riskStyles = {
  "Low Risk": "bg-wgreen-100 text-wgreen-700 border border-[rgba(15,122,74,0.25)]",
  "Medium Risk": "bg-gold-100 text-gold-700 border border-[rgba(154,107,30,0.25)]",
  "High Risk": "bg-[#FEE4E2] text-[#B42318] border border-[rgba(180,35,24,0.25)]",
  "Critical Risk": "bg-[#FEE4E2] text-[#7A1710] border border-[rgba(122,23,16,0.35)]",
};

export const RiskBadge = ({ rating, testId }) => (
  <span data-testid={testId} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${riskStyles[rating] || riskStyles["Medium Risk"]}`}>
    <CircleDot className="h-3 w-3" /> {rating}
  </span>
);

export const EvidenceBadge = ({ level }) => {
  const strong = ["Implemented", "Document reviewed", "Records available"].includes(level);
  const missing = level === "No evidence";
  const cls = strong
    ? "bg-wgreen-100 text-wgreen-700"
    : missing
      ? "bg-[#FEE4E2] text-[#B42318]"
      : "bg-[#EEF2F8] text-navy-700";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${cls}`}>
      {level}
    </span>
  );
};

const statusMap = {
  Active: { cls: "bg-wgreen-100 text-wgreen-700 border border-[rgba(15,122,74,0.25)]", Icon: ShieldCheck },
  Expired: { cls: "bg-[#EEF2F8] text-[#5B6B84] border border-[#E3E8F2]", Icon: Clock },
  Suspended: { cls: "bg-[#FFF1E6] text-[#9A3412] border border-[rgba(154,52,18,0.25)]", Icon: PauseCircle },
  Revoked: { cls: "bg-[#FEE4E2] text-[#B42318] border border-[rgba(180,35,24,0.25)]", Icon: Ban },
};

export const StatusBadge = ({ status, testId }) => {
  const { cls, Icon } = statusMap[status] || statusMap.Active;
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold ${cls}`}>
      <Icon className="h-4 w-4" /> {status}
    </span>
  );
};

const appStatusMap = {
  draft: { label: "Draft", cls: "bg-[#EEF2F8] text-navy-700" },
  ai_reviewed: { label: "AI Reviewed", cls: "bg-gold-100 text-gold-700" },
  submitted: { label: "Submitted", cls: "bg-[#E6F0FB] text-[#1E5AA8]" },
  paid: { label: "Paid / Ready for Review", cls: "bg-wgreen-100 text-wgreen-700" },
  in_review: { label: "In Review", cls: "bg-[#E6F0FB] text-[#1E5AA8]" },
  needs_info: { label: "Needs Info", cls: "bg-[#FFF1E6] text-[#9A3412]" },
  enhanced_review: { label: "Enhanced Review", cls: "bg-[#FFF1E6] text-[#9A3412]" },
  approved: { label: "Approved", cls: "bg-wgreen-100 text-wgreen-700" },
  rejected: { label: "Rejected", cls: "bg-[#FEE4E2] text-[#B42318]" },
  certified: { label: "Certified", cls: "bg-wgreen-100 text-wgreen-700" },
};

export const AppStatusBadge = ({ status, testId }) => {
  const s = appStatusMap[status] || { label: status, cls: "bg-[#EEF2F8] text-navy-700" };
  return (
    <span data-testid={testId} className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${s.cls}`}>
      {s.label}
    </span>
  );
};

const paymentMap = {
  unpaid: { label: "Payment due", cls: "bg-[#FFF1E6] text-[#9A3412]" },
  processing: { label: "Payment processing", cls: "bg-gold-100 text-gold-700" },
  paid: { label: "Paid", cls: "bg-wgreen-100 text-wgreen-700" },
  failed: { label: "Payment failed", cls: "bg-[#FEE4E2] text-[#B42318]" },
};

export const PaymentBadge = ({ status, testId }) => {
  if (!status) return null;
  const s = paymentMap[status] || { label: status, cls: "bg-[#EEF2F8] text-navy-700" };
  return (
    <span data-testid={testId} className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${s.cls}`}>
      {s.label}
    </span>
  );
};
